import moment from "moment";

type BusinessDateInput = moment.MomentInput;

/**
 * @method isBusinessDay
 * @description Check if a date is a business day (Mon-Fri)
 * @param {moment.Moment|string|Date} date
 * @returns {boolean}
 */
export const isBusinessDay = (date: BusinessDateInput): boolean => {
  const day = moment(date).day();
  return day !== 0 && day !== 6;
};

/**
 * @method nextBusinessDay
 * @description Get next business day from a date
 * @param {moment.Moment|string|Date} date
 * @returns {moment.Moment}
 */
export const nextBusinessDay = (date: BusinessDateInput): moment.Moment => {
  let next = moment(date).add(1, "day");
  while (next.day() === 0 || next.day() === 6) {
    next.add(1, "day");
  }
  return next;
};

/**
 * @method businessAdd
 * @description Add business days to a date
 * @param {moment.Moment|string|Date} date
 * @param {number} days
 * @returns {moment.Moment}
 */
export const businessAdd = (
  date: BusinessDateInput,
  days: number
): moment.Moment => {
  let current = moment(date);
  let remaining = days;
  while (remaining > 0) {
    current.add(1, "day");
    if (isBusinessDay(current)) {
      remaining--;
    }
  }
  // Ensure result lands on a business day
  while (!isBusinessDay(current)) {
    current.add(1, "day");
  }
  return current;
};

/**
 * @method getMonthBusinessDays
 * @description Get all business days (Mon-Fri) in a month
 * @param {moment.Moment|string|Date} date
 * @returns {Array<moment.Moment>}
 */
export const getMonthBusinessDays = (
  date: BusinessDateInput
): moment.Moment[] => {
  const startOfMonth = moment(date).startOf("month");
  const endOfMonth = moment(date).endOf("month");
  const businessDays: moment.Moment[] = [];

  let current = startOfMonth.clone();
  while (current.isSameOrBefore(endOfMonth)) {
    const dayOfWeek = current.day();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays.push(current.clone());
    }
    current.add(1, "day");
  }

  return businessDays;
};

/**
 * @method businessSubtract
 * @description Subtract business days from a date
 * @param {moment.Moment|string|Date} date
 * @param {number} days
 * @returns {moment.Moment}
 */
export const businessSubtract = (
  date: BusinessDateInput,
  days: number
): moment.Moment => {
  let current = moment(date);
  let remaining = days;
  while (remaining > 0) {
    current.subtract(1, "day");
    if (isBusinessDay(current)) {
      remaining--;
    }
  }
  while (!isBusinessDay(current)) {
    current.subtract(1, "day");
  }
  return current;
};
